import React, { useState, useCallback, useRef } from 'react';
import ConfirmationModal from '@/shared/components/ui/ConfirmationModal';
import { ButtonVariant } from '@/shared/ui/Button';
import { useTranslation } from 'react-i18next';

interface ConfirmDialogOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: ButtonVariant;
}

/**
 * Replaces window.confirm with the app's styled ConfirmationModal.
 *
 * Usage:
 * ```tsx
 * const { confirm, ConfirmDialog } = useConfirmDialog();
 *
 * const handleClose = async () => {
 *   if (isDirty) {
 *     const confirmed = await confirm({ message: 'You have unsaved changes...' });
 *     if (!confirmed) return;
 *   }
 *   onClose();
 * };
 *
 * return <>
 *   {children}
 *   <ConfirmDialog />
 * </>;
 * ```
 */
export const useConfirmDialog = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmDialogOptions>({});
  // Use ref to avoid stale closure issues with resolve callback
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmDialogOptions = {}): Promise<boolean> => {
    setOptions(opts);
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  // Stable component — no useCallback wrapper, just a plain function component
  const ConfirmDialog = useCallback(() => (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={handleCancel}
      onConfirm={handleConfirm}
      title={options.title || t('common.unsavedChanges.title', 'Unsaved Changes')}
      message={options.message || t('common.unsavedChanges.message', 'You have unsaved changes. Are you sure you want to discard them?')}
      confirmText={options.confirmText || t('common.unsavedChanges.leave', 'Discard')}
      cancelText={options.cancelText || t('common.unsavedChanges.stay', 'Cancel')}
      confirmVariant={options.confirmVariant || ButtonVariant.Destructive}
    />
  ), [isOpen, options, handleConfirm, handleCancel, t]);

  return { confirm, ConfirmDialog, isConfirmOpen: isOpen };
};
